import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { SetAvailabilityInput, StaffMenu } from "@luckymenu/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { availabilityOf, withAvailability, withItem, withModifierGroup } from "../menu/cache";
import { type MenuProblem, menuProblemOf } from "../menu/problems";
import { type MenuSection, menuSections } from "../menu/view";
import { useActiveRestaurant, useStaffCore, useSupportSession } from "../provider";

/**
 * The Menu tab, shared by both staff apps: the whole menu from
 * `staff.catalog.listMenu`, search, and availability switches. Editing hooks
 * are in the form hooks; they all update this one cached menu. The device's
 * copy of the menu shows first (menu/device-cache.ts), so the Menu tab and
 * the item picker open without waiting, and without a connection.
 */
export function useMenu() {
	const trpc = useTRPC();
	const core = useStaffCore();
	const restaurantId = useActiveRestaurant((s) => s.restaurantId);
	const support = useSupportSession((s) => s.session?.restaurantId ?? null);
	useEffect(() => {
		void restaurantId;
		void support;
		void core.menuCache.hydrate();
	}, [core, restaurantId, support]);
	return useQuery(trpc.staff.catalog.listMenu.queryOptions());
}

/** Writes to the cached menu. Every menu hook goes through this. */
export function useMenuCache() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const queryKey = useMemo(() => trpc.staff.catalog.listMenu.queryKey(), [trpc]);
	return useMemo(
		() => ({
			queryKey,
			get: () => queryClient.getQueryData<StaffMenu>(queryKey),
			update: (change: (menu: StaffMenu) => StaffMenu) =>
				queryClient.setQueryData<StaffMenu>(queryKey, (old) => (old ? change(old) : old)),
			/** Stops a refetch in flight from overwriting an optimistic change. */
			cancel: () => queryClient.cancelQueries({ queryKey }),
			/** Fetches the menu again, to pick up what the server decided. */
			refresh: () => queryClient.invalidateQueries({ queryKey }),
		}),
		[queryClient, queryKey],
	);
}

/** The search box and the sections it leaves. */
export function useMenuSearch(menu: StaffMenu | undefined) {
	const [query, setQuery] = useState("");
	const sections: MenuSection[] = useMemo(
		() => (menu ? menuSections(menu, query) : []),
		[menu, query],
	);
	return { query, setQuery, sections, searching: query.trim() !== "" };
}

/** A switch the server refused, already turned back, with the reason. */
export type AvailabilityProblem = SetAvailabilityInput & { problem: MenuProblem };

/**
 * Sold out and back for items, sizes and add-on options (OP-16; every role).
 * The switch moves at once; if the server refuses, that one row moves back
 * and `problem` says why.
 */
export function useAvailability() {
	const trpc = useTRPC();
	const cache = useMenuCache();
	const [problem, setProblem] = useState<AvailabilityProblem | null>(null);
	const mutation = useMutation(
		trpc.staff.catalog.setAvailability.mutationOptions({
			onMutate: async (input) => {
				await cache.cancel();
				const menu = cache.get();
				const before = menu ? availabilityOf(menu, input) : null;
				cache.update((m) => withAvailability(m, input));
				return { before };
			},
			onError: (error, input, context) => {
				const before = (context as { before: boolean | null } | undefined)?.before;
				if (typeof before === "boolean")
					cache.update((m) => withAvailability(m, { ...input, isAvailable: before }));
				setProblem({ ...input, problem: menuProblemOf(error) });
			},
			onSuccess: (result) => {
				cache.update((m) =>
					result.kind === "item"
						? withItem(m, result.item)
						: withModifierGroup(m, result.modifierGroup),
				);
			},
		}),
	);
	const set = useCallback(
		(input: SetAvailabilityInput) => {
			setProblem(null);
			mutation.mutate(input);
		},
		[mutation.mutate],
	);
	return { set, problem, clearProblem: useCallback(() => setProblem(null), []) };
}
